import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { storyElement } from "@/lib/story-element";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  User, Settings, LogOut, Loader2, Sparkles, Check, AlertTriangle, X,
  Users, ChevronDown, LogIn, Shield, Coins, ArrowLeft,
  Wallet, Send, TrendingDown, AlertCircle, Languages, Globe,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import logoSvg from "@/assets/logo.svg";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { useUserRole } from "@/hooks/useUserRole";
import { toast } from "sonner";
import { useState, useEffect, useSyncExternalStore, useCallback } from "react";
import {
  getGenerationTasks,
  subscribeToTasks,
  removeGenerationTask,
  clearAllTasks,
  type GenerationTask,
} from "@/lib/generation-task-store";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { NotificationBell } from "@/components/social/NotificationBell";
import { useLocale } from "@/hooks/useLocale";
import { LOCALE_SETTING_FULL_LABELS, LOCALE_SETTING_LABELS, type LocaleSetting } from "@/lib/i18n/types";
import { EditorPlaySwitcher } from "@/components/play/EditorPlaySwitcher";
import { BuilderVersionSwitch } from "@/components/world/BuilderVersionSwitch";
import { useIsMobile } from "@/hooks/use-mobile";

interface Profile {
  full_name: string | null;
  avatar_url: string | null;
}

function useGenerationTasks() {
  const subscribe = useCallback((cb: () => void) => subscribeToTasks(cb), []);
  const getSnapshot = useCallback(() => {
    const tasks = getGenerationTasks();
    return JSON.stringify(tasks);
  }, []);
  const raw = useSyncExternalStore(subscribe, getSnapshot);
  return JSON.parse(raw) as GenerationTask[];
}

function formatElapsed(startedAt: number): string {
  const s = Math.floor((Date.now() - startedAt) / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

interface AppHeaderProps {
  /** "normal" = nav links (default for most pages), "extended" = credits + worlds + notifications (Play/Builder) */
  variant?: "normal" | "extended";
}

const AppHeader = ({ variant = "normal" }: AppHeaderProps) => {
  const { t, localeSetting, setLocale: setAppLocale } = useLocale();
  // Localized label for a locale setting option (translates "auto" via t.locale_auto_detect).
  const localeLabel = (opt: import("@/lib/i18n/types").LocaleSetting) =>
    opt === "auto" ? t.locale_auto_detect : LOCALE_SETTING_FULL_LABELS[opt];
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { isSuperAdmin, isLoading: rolesLoading } = useUserRole();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { balance: creditBalance, thresholds: creditThresholds } = useCreditBalance();
  const tasks = useGenerationTasks();
  const runningTasks = tasks.filter((t) => t.status === "running");
  const recentTasks = tasks.filter((t) => t.status !== "running");

  const isExtended = variant === "extended";

  const PHASE_LABELS: Record<string, string> = {
    search: t.task_searching,
    brief: t.task_writing_brief,
    image: t.task_generating,
  };

  const [, setTick] = useState(0);
  useEffect(() => {
    if (runningTasks.length === 0) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [runningTasks.length]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const user = await getSessionUser();
        if (!user) { setIsLoading(false); setIsLoggedIn(false); return; }
        setIsLoggedIn(true);
        setUserEmail(user.email ?? null);
        const { data, error } = await supabase
          .from("profiles")
          .select("full_name, avatar_url")
          .eq("id", user.id)
          .single();
        if (error) console.error("Error fetching profile:", error);
        else setProfile(data);
      } catch (error) {
        console.error("Error in fetchProfile:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(t.sign_out_failed, { description: error.message });
    } else {
      navigate("/auth");
    }
  };

  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    }
    return userEmail?.[0]?.toUpperCase() || "U";
  };

  const isActive = (path: string) => location.pathname === path;

  // Detect Editor⇄Play context from current route so we can render the
  // quick switcher in the mobile header (replacing the language switcher).
  const worldMatch = location.pathname.match(/^\/world\/([^/]+)/);
  const playMatch = location.pathname.match(/^\/play\/([^/]+)/);
  const adventureFromQuery = searchParams.get("adventure");
  const [playCtxWorldId, setPlayCtxWorldId] = useState<string | null>(null);
  const playAdventureId = playMatch ? playMatch[1] : null;

  useEffect(() => {
    let cancelled = false;
    if (!playAdventureId) { setPlayCtxWorldId(null); return; }
    supabase
      .from("adventures")
      .select("world_id")
      .eq("id", playAdventureId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setPlayCtxWorldId((data?.world_id as string) || null);
      });
    return () => { cancelled = true; };
  }, [playAdventureId]);

  const switcherCtx: { worldId: string; adventureId: string; current: "editor" | "play" } | null =
    worldMatch && adventureFromQuery
      ? { worldId: worldMatch[1], adventureId: adventureFromQuery, current: "editor" }
      : (playAdventureId && playCtxWorldId)
        ? { worldId: playCtxWorldId, adventureId: playAdventureId, current: "play" }
        : null;

  return (
    <header {...storyElement("app-header")} className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 max-w-screen-2xl items-center px-4">
        {/* Left: Logo + nav */}
        <div className="flex items-center gap-1">
          <div
            onClick={() => navigate("/")}
            className="flex items-center gap-2 cursor-pointer px-2"
          >
            <img src={logoSvg} alt="Adventure Venture" style={{ width: 50, height: 45 }} />
            <span className="hidden sm:flex flex-col leading-none font-bold text-xs tracking-tight">
              <span className="ml-0">ADVENTURE</span>
              <span className="ml-2">VENTURE</span>
            </span>
          </div>

          <div className="hidden sm:block h-5 w-px bg-border mx-1" />

          {/* Worlds button — always visible */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className={cn(
              "gap-1.5 hover:bg-accent mx-0.5",
              isActive("/") && "bg-accent text-accent-foreground"
            )}
          >
            <Globe className="w-3.5 h-3.5" />
            {t.nav_worlds}
          </Button>

          {/* Full nav links */}
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/explore")}
              className={cn("hidden sm:inline-flex hover:bg-accent mx-0.5", isActive("/explore") && "bg-accent text-accent-foreground")}
            >
              {t.nav_explore}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/about")}
              className={cn("hidden sm:inline-flex hover:bg-accent mx-0.5", isActive("/about") && "bg-accent text-accent-foreground")}
            >
              {t.nav_about}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "hidden sm:inline-flex gap-1 hover:bg-accent mx-0.5",
                    (isActive("/community") || isActive("/messages")) && "bg-accent text-accent-foreground"
                  )}
                >
                  {t.nav_community}
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => navigate("/community")} className="cursor-pointer gap-2">
                  {t.nav_forums}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/messages")} className="cursor-pointer gap-2">
                  {t.nav_messages}
                </DropdownMenuItem>
                {/* W19: removed duplicate "/community" link that was mislabeled as "Users".
                    There is no public user directory — individual profiles use /user/:userId. */}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/roadmap")}
              className={cn("hidden sm:inline-flex hover:bg-accent mx-0.5", isActive("/roadmap") && "bg-accent text-accent-foreground")}
            >
              {t.nav_roadmap}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/support")}
              className={cn("hidden sm:inline-flex hover:bg-accent mx-0.5", isActive("/support") && "bg-accent text-accent-foreground")}
            >
              {t.nav_pricing}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/contact")}
              className={cn("hidden sm:inline-flex hover:bg-accent mx-0.5", isActive("/contact") && "bg-accent text-accent-foreground")}
            >
              {t.nav_contact}
            </Button>
          </>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-1">
          {/* Extended: Credits display */}
          {isLoggedIn && creditBalance !== null && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "gap-1 text-xs tabular-nums h-8 px-2",
                    !creditThresholds.canUseCredits && "text-destructive",
                    creditThresholds.canUseCredits && !creditThresholds.canBuild && "text-orange-500",
                    creditThresholds.canBuild && !creditThresholds.canTransfer && "text-yellow-500",
                  )}
                  title={t.nav_your_credits}
                >
                  <Coins className={cn(
                    "w-3.5 h-3.5",
                    !creditThresholds.canUseCredits ? "text-destructive" : "text-primary"
                  )} />
                  <span className="font-mono">{creditBalance < 10 ? creditBalance.toFixed(2) : creditBalance.toFixed(0)}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-0">
                <div className="px-3 py-2.5 border-b border-border/40">
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-amber-500" />
                    <div>
                      <p className="text-sm font-semibold font-mono">{creditBalance.toFixed(2)} <span className="text-muted-foreground font-normal text-xs">{t.hdr_credits}</span></p>
                    </div>
                  </div>
                  {!creditThresholds.canUseCredits && (
                    <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-destructive">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      <span>{t.hdr_ai_disabled}</span>
                    </div>
                  )}
                  {creditThresholds.canUseCredits && !creditThresholds.canBuild && (
                    <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-orange-500">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      <span>{t.hdr_building_disabled}</span>
                    </div>
                  )}
                </div>
                <div className="py-1">
                  <button
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-accent transition-colors text-left"
                    onClick={() => navigate("/settings?tab=credits")}
                  >
                    <Wallet className="w-3.5 h-3.5 text-muted-foreground" />
                    {t.hdr_credit_wallet}
                  </button>
                  <button
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-accent transition-colors text-left"
                    onClick={() => navigate("/profile?tab=credits")}
                  >
                    <TrendingDown className="w-3.5 h-3.5 text-muted-foreground" />
                    {t.hdr_usage_dashboard}
                  </button>
                  <button
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-accent transition-colors text-left"
                    onClick={() => navigate("/settings?tab=pricing")}
                  >
                    <Coins className="w-3.5 h-3.5 text-muted-foreground" />
                    {t.hdr_pricing_rates}
                  </button>
                  <button
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-accent transition-colors text-left"
                    onClick={() => navigate("/settings?tab=referrals")}
                  >
                    <Send className="w-3.5 h-3.5 text-muted-foreground" />
                    {t.hdr_earn_credits}
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Mobile: quick Editor⇄Play switcher in place of language switcher */}
          {isMobile && switcherCtx && (
            <EditorPlaySwitcher
              worldId={switcherCtx.worldId}
              adventureId={switcherCtx.adventureId}
              current={switcherCtx.current}
              compact
            />
          )}

          {/* World Builder: V1/V2 UI version switch */}
          {isExtended && worldMatch && !switcherCtx && (
            <BuilderVersionSwitch className="hidden sm:inline-flex" />
          )}

          {/* Language Switcher — desktop/tablet only; on mobile it lives inside the avatar/guest menu */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex gap-1 h-8 px-2 text-xs">
                <Languages className="w-3.5 h-3.5" />
                <span className="font-medium">{LOCALE_SETTING_LABELS[localeSetting]}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {(["auto", "en", "he", "ru"] as LocaleSetting[]).map((opt) => (
                <DropdownMenuItem
                  key={opt}
                  onClick={() => setAppLocale(opt)}
                  className={cn("cursor-pointer gap-2 text-sm", localeSetting === opt && "bg-accent")}
                >
                  {localeSetting === opt && <Check className="w-3.5 h-3.5 text-primary" />}
                  {localeSetting !== opt && <span className="w-3.5" />}
                  {localeLabel(opt)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Extended: Notifications */}
          {isExtended && <NotificationBell />}

          {/* Guest: Login / Join Waitlist buttons */}
          {!isExtended && !isLoading && !isLoggedIn && (
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/auth?mode=login")}
                className="text-xs"
              >
                {t.nav_sign_in_up?.split("/")[0]?.trim() || "Sign in"}
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/auth?mode=signup")}
                className="text-xs"
              >
                {t.nav_sign_in_up?.split("/")[1]?.trim() || "Join Waitlist"}
              </Button>
            </div>
          )}

          {/* Avatar dropdown */}
          {(isLoading || isLoggedIn || isExtended) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                  {isLoading || rolesLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : isLoggedIn ? (
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name || "User"} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-muted text-muted-foreground text-xs">
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                  )}
                  {runningTasks.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground ring-2 ring-background">
                      {runningTasks.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72" align="end">
                {!isLoggedIn && !isLoading ? (
                  <>
                    <DropdownMenuLabel className="font-normal text-sm text-muted-foreground">
                      {t.nav_guest_browsing}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/auth")} className="cursor-pointer gap-2">
                      <LogIn className="h-4 w-4" />
                      {t.nav_sign_in_up}
                    </DropdownMenuItem>
                    {/* Mobile-only language selector for guests */}
                    <div className="sm:hidden">
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <Languages className="w-3 h-3" />
                        {t.settings_language}
                      </DropdownMenuLabel>
                      {(["auto", "en", "he", "ru"] as LocaleSetting[]).map((opt) => (
                        <DropdownMenuItem
                          key={opt}
                          onClick={() => setAppLocale(opt)}
                          className={cn("cursor-pointer gap-2 text-sm", localeSetting === opt && "bg-accent")}
                        >
                          {localeSetting === opt ? (
                            <Check className="w-3.5 h-3.5 text-primary" />
                          ) : (
                            <span className="w-3.5" />
                          )}
                          {localeLabel(opt)}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{profile?.full_name || t.nav_set_display_name}</p>
                        <p className="text-xs leading-none text-muted-foreground">{userEmail || ""}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer gap-2">
                      <User className="h-4 w-4" />
                      {t.profile}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer gap-2">
                      <Settings className="h-4 w-4" />
                      {t.settings}
                    </DropdownMenuItem>
                    {isSuperAdmin && (
                      <DropdownMenuItem onClick={() => navigate("/admin/users")} className="cursor-pointer gap-2">
                        <Shield className="h-4 w-4" />
                        {t.nav_user_management}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/profile?tab=friends")} className="cursor-pointer gap-2">
                      <Users className="h-4 w-4" />
                      {t.profile_friends}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {(() => {
                      try {
                        const stored = localStorage.getItem("admin_impersonate_return");
                        if (stored) {
                          const returnData = JSON.parse(stored);
                          return (
                            <DropdownMenuItem
                              className="cursor-pointer gap-2 text-amber-600 dark:text-amber-400"
                              onClick={async () => {
                                try {
                                  await supabase.auth.setSession({
                                    access_token: returnData.access_token,
                                    refresh_token: returnData.refresh_token,
                                  });
                                  localStorage.removeItem("admin_impersonate_return");
                                  window.location.href = "/admin/users";
                                } catch {
                                  localStorage.removeItem("admin_impersonate_return");
                                  window.location.href = "/auth";
                                }
                              }}
                            >
                              <ArrowLeft className="h-4 w-4" />
                              Return to original user
                            </DropdownMenuItem>
                          );
                        }
                      } catch {}
                      return null;
                    })()}
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive gap-2">
                      <LogOut className="h-4 w-4" />
                      {t.nav_sign_out}
                    </DropdownMenuItem>
                    {/* Mobile-only language selector for signed-in users */}
                    <div className="sm:hidden">
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <Languages className="w-3 h-3" />
                        {t.settings_language}
                      </DropdownMenuLabel>
                      {(["auto", "en", "he", "ru"] as LocaleSetting[]).map((opt) => (
                        <DropdownMenuItem
                          key={opt}
                          onClick={() => setAppLocale(opt)}
                          className={cn("cursor-pointer gap-2 text-sm", localeSetting === opt && "bg-accent")}
                        >
                          {localeSetting === opt ? (
                            <Check className="w-3.5 h-3.5 text-primary" />
                          ) : (
                            <span className="w-3.5" />
                          )}
                          {localeLabel(opt)}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </>
                )}

                {tasks.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="flex items-center justify-between">
                      <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3" />
                        {t.nav_active_tasks}
                        {runningTasks.length > 0 && (
                          <Badge variant="secondary" className="h-4 px-1 text-[9px] rounded-full">
                            {runningTasks.length}
                          </Badge>
                        )}
                      </span>
                      {recentTasks.length > 0 && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearAllTasks(); }}
                          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {t.nav_clear}
                        </button>
                      )}
                    </DropdownMenuLabel>
                    <div className="max-h-48 overflow-y-auto px-1 py-0.5 space-y-1">
                      {tasks.map((task) => (
                        <button
                          key={task.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/world/${task.worldId}?openEntity=${task.entityId}`);
                          }}
                          className={cn(
                            "flex items-start gap-2 px-2 py-1.5 rounded-md text-xs w-full text-left cursor-pointer hover:bg-accent/60 transition-colors",
                            task.status === "running" && "bg-primary/5 border border-primary/20",
                            task.status === "done" && "bg-accent/50 opacity-70",
                            task.status === "error" && "bg-destructive/10 border border-destructive/20",
                          )}
                        >
                          {task.status === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0 mt-0.5" />}
                          {task.status === "done" && <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />}
                          {task.status === "error" && <AlertTriangle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-1">
                              <span className="font-medium truncate">{task.entityName}</span>
                              <span className="text-[10px] text-muted-foreground shrink-0">{formatElapsed(task.startedAt)}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {task.status === "running" ? (
                                <>
                                  <span className="text-primary/80 font-medium">{PHASE_LABELS[task.phase] || task.phase}</span>
                                  {task.detail && <span className="ml-1">{task.detail}</span>}
                                </>
                              ) : task.status === "error" ? (
                                <span className="text-destructive">{task.error || t.task_failed}</span>
                              ) : (
                                <span>{t.task_complete}</span>
                              )}
                            </div>
                            {task.status === "running" && task.query && (
                              <div className="text-[10px] text-muted-foreground/60 truncate mt-0.5">
                                {task.provider && <span className="font-medium">{task.provider}</span>}
                                {task.query && <span> → "{task.query}"</span>}
                              </div>
                            )}
                          </div>
                          {task.status !== "running" && (
                            <span
                              role="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeGenerationTask(task.id); }}
                              className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
                            >
                              <X className="w-3 h-3" />
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
